import type { SxProps, Theme } from "@mui/material";

/**
 * UI-only chip styling for platform enums - pastel recipe borrowed from the
 * User Management status/role chips: light tinted background + saturated
 * letters (global MuiChip override supplies the pill radius).
 */

export const PASTEL_CHIP_BASE_SX = {
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: 0.5,
} as const;

const NEUTRAL_CHIP_SX = {
  bgcolor: "rgba(0,0,0,0.08)",
  color: "text.secondary",
} as const;

export const RUN_STATUS_CHIP_SX: Record<string, SxProps<Theme>> = {
  PENDING: { bgcolor: "rgba(255,152,0,0.12)", color: "warning.dark" },
  RUNNING: { bgcolor: "rgba(2,136,209,0.12)", color: "info.dark" },
  SUCCESS: { bgcolor: "rgba(46,125,50,0.12)", color: "success.dark" },
  FAILED: { bgcolor: "rgba(211,47,47,0.10)", color: "error.dark" },
  SKIPPED: NEUTRAL_CHIP_SX,
};

// Stage colors reuse the UM role-chip palette (blue / purple / teal).
export const STAGE_CHIP_SX: Record<string, SxProps<Theme>> = {
  SOURCE: { bgcolor: "#e3f2fd", color: "#1565c0" },
  TRANSFORM: { bgcolor: "#f3e5f5", color: "#6a1b9a" },
  DESTINATION: { bgcolor: "#e0f2f1", color: "#00695c" },
};

// Client-facing statuses (CAL-5773), same recipe. "Awaiting confirmation" is
// amber and deliberately never red: it is a neutral placeholder while
// Calibrate services the run, not an error the client should act on. Red is
// "Failed" alone - only ever set by staff, never derived (CAL-6165).
export const CLIENT_RUN_STATUS_CHIP_SX: Record<string, SxProps<Theme>> = {
  RUNNING: RUN_STATUS_CHIP_SX.RUNNING,
  SUCCESS: RUN_STATUS_CHIP_SX.SUCCESS,
  AWAITING_CONFIRMATION: {
    bgcolor: "rgba(255,152,0,0.12)",
    color: "warning.dark",
  },
  FAILED: RUN_STATUS_CHIP_SX.FAILED,
};

export function runStatusChipSx(status: string): SxProps<Theme> {
  return RUN_STATUS_CHIP_SX[status] ?? NEUTRAL_CHIP_SX;
}

export function clientRunStatusChipSx(status: string): SxProps<Theme> {
  return CLIENT_RUN_STATUS_CHIP_SX[status] ?? NEUTRAL_CHIP_SX;
}

export function stageChipSx(stage: string): SxProps<Theme> {
  return STAGE_CHIP_SX[stage] ?? NEUTRAL_CHIP_SX;
}
